import { defineStore } from 'pinia'
import { ref, watch } from 'vue'
import { useOnline } from '@vueuse/core'

import { getHealthCheck } from 'src/services/api-client'
import { DateTime } from 'luxon'

export const useNetworkStore = defineStore('network', () => {
  const isNavigatorOnline = useOnline()
  const offlineMode = ref<boolean | null>(null)
  const timeoutId = ref<number | null>(null)
  const checkInterval = 30000

  const lastOnline = ref<DateTime | null>(null)
  const lastAttempt = ref<DateTime | null>(null)

  const connectivityLoading = ref<boolean>(false)
  const connectivityRunning = ref<boolean>(false)

  async function checkConnectivity() {
    lastAttempt.value = DateTime.now()
    connectivityLoading.value = true

    try {
      const ok = await getHealthCheck()
      if (ok) {
        lastOnline.value = DateTime.now()
        lastAttempt.value = null
      }
      return ok
    } catch (err) {
      return false
    } finally {
      connectivityLoading.value = false
    }
  }

  async function runCheck() {
    if (connectivityRunning.value) return
    connectivityRunning.value = true

    await checkConnectivity()
    connectivityRunning.value = false
  }

  function startTimer() {
    if (timeoutId.value !== null) return

    timeoutId.value = window.setTimeout(async function tick() {
      timeoutId.value = null
      await runCheck()
      if (offlineMode.value) startTimer()
    }, checkInterval)
  }

  function clearTimer() {
    if (timeoutId.value !== null) {
      window.clearTimeout(timeoutId.value)
      timeoutId.value = null
    }
  }

  async function forceCheck() {
    clearTimer()
    await runCheck().then(() => {
      if (offlineMode.value) startTimer()
    })
  }

  watch(
    offlineMode,
    async (isOffline, wasOffline) => {
      if (isOffline && !wasOffline) startTimer()
      if (!isOffline && wasOffline) clearTimer()
    },
    { immediate: true },
  )

  return {
    connectivityLoading,
    connectivityRunning,
    offlineMode,
    lastAttempt,
    lastOnline,
    forceCheck,
    isNavigatorOnline,
  }
})
